import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { ApiService } from '../../services/api.service';
import { CompanyService } from '../services/company.service';
import {
  loadCompaniesAndSelectFirst,
  toggleCompanyDropdown,
  closeCompanyDropdown,
} from '../utils/company.util';
import { countActiveFilterValues } from '../utils/filter-count.util';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../shared/components/pagination-bar/pagination-bar.component';
import { EmptyResultComponent } from '../../shared/components/empty-result/empty-result.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { TitleBarComponent } from '../../shared/components/title-bar/title-bar.component';
import { SearchbarComponent } from '../../shared/components/searchbar/searchbar.component';
import { ActionButtonComponent } from '../../shared/components/action-button/action-button.component';
import {
  FilterModalComponent,
  FilterCategory,
} from '../../shared/components/filter-modal/filter-modal.component';
import {
  SimpleDropdownComponent,
  DropdownOption,
} from '../../shared/components/simple-dropdown/simple-dropdown.component';
import { TableUtils, DragScrollConfig } from '../../orders/utils/table.utils';
import {
  buildPaginationData,
  calculateTotalPages,
  applyPageChange,
  applyItemsPerPageChange,
} from '../../orders/utils/orders-page-shared.utils';
import { CampaignPerformanceCellComponent } from '../../shared/components/campaign-performance-cell/campaign-performance-cell.component';

/**
 * Get the default campaign filters object.
 * @example
 * getDefaultCampaignFilters()
 * {
 *   status: {
 *     active: false,
 *     scheduled: false,
 *     completed: false,
 *     draft: false
 *   },
 *   platform: {
 *     email: false,
 *     sms: false,
 *     facebook: false,
 *     google: false
 *   }
 * }
 * @returns {Object} Default filters object with status and platform boolean flags.
 **/
function getDefaultCampaignFilters() {
  return {
    status: {
      active: false,
      scheduled: false,
      completed: false,
      draft: false,
    },
    platform: {
      email: false,
      sms: false,
      facebook: false,
      google: false,
    },
  };
}

interface CampaignTableRow {
  id: string;
  name: string;
  status: string;
  promoCode: string;
  offerType: string;
  discount: string;
  startDate: string;
  endDate: string;
  platforms: string[];
  locations: string[];
  segments: string[];
  performance?: {
    campaign_id: string;
    clicks: string;
    conversions: string;
    delivered: string;
    failed: string;
    impressions: string;
    opens: string;
    sent: string;
    total_revenue?: string;
  };
  goals?: {
    id: string;
    campaign_id: string;
    achieved: number;
    created_at: string;
    end_date: string | null;
    event: string;
    goal_name: string;
    start_date: string | null;
    status: boolean;
    target_value: number;
    timeframe: string;
    updated_at: string;
  }[];
}

@Component({
  selector: 'app-admin-campaigns',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PaginationBarComponent,
    EmptyResultComponent,
    LoadingTableShimmerComponent,
    TitleBarComponent,
    SearchbarComponent,
    ActionButtonComponent,
    FilterModalComponent,
    SimpleDropdownComponent,
    CampaignPerformanceCellComponent,
  ],
  templateUrl: './admin-campaigns.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./admin-campaigns.component.scss'],
})
export class AdminCampaignsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tableScrollWrapper') tableScrollWrapper?: ElementRef<HTMLElement>;

  searchTerm = '';
  itemsPerPage = 10;
  currentPage = 1;
  totalItems = 0;
  loading = true;

  // Company dropdown properties
  companyOptions: DropdownOption[] = [];
  selectedCompanyId = '';
  isCompanyDropdownOpen = false;

  campaigns: CampaignTableRow[] = [];
  showFilterModal = false;

  filterCategories: FilterCategory[] = [
    {
      id: 'status',
      name: 'Status',
      options: [
        { id: 'active', label: 'Active', value: 'active', selected: false },
        { id: 'scheduled', label: 'Scheduled', value: 'scheduled', selected: false },
        { id: 'completed', label: 'Completed', value: 'completed', selected: false },
        { id: 'draft', label: 'Draft', value: 'draft', selected: false },
      ],
    },
    {
      id: 'platform',
      name: 'Platform',
      options: [
        { id: 'email', label: 'Email', value: 'email', selected: false },
        { id: 'sms', label: 'SMS', value: 'sms', selected: false },
        { id: 'facebook', label: 'Facebook', value: 'facebook', selected: false },
        { id: 'google', label: 'Google', value: 'google', selected: false },
      ],
    },
  ];

  shimmerColumns: ShimmerColumn[] = [
    { label: 'Campaign Name', width: 'md' },
    { label: 'Status', width: 'sm' },
    { label: 'Promo Code', width: 'sm' },
    { label: 'Start Date', width: 'md' },
    { label: 'End Date', width: 'md' },
    { label: 'Platforms', width: 'sm' },
    { label: 'Locations', width: 'sm' },
    { label: 'Performance', width: 'md' },
    { label: 'Goals', width: 'md' },
  ];

  appliedFilters: any = getDefaultCampaignFilters();

  // Sorting functionality
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | 'none' = 'none';

  // Drag scrolling configuration
  private dragScrollConfig!: DragScrollConfig;

  private readonly apiService = inject(ApiService);
  private readonly companyService = inject(CompanyService);
  private readonly messageService = inject(NotificationService);
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.loadCompanies();
  }

  ngAfterViewInit(): void {
    this.dragScrollConfig =
      TableUtils.reinitializeDragScrolling(this.tableScrollWrapper) ?? this.dragScrollConfig;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    TableUtils.cleanupTableScrolling();
  }

  private loadCompanies(): void {
    loadCompaniesAndSelectFirst(this.companyService, this.messageService, {
      setCompanyOptions: opts => {
        this.companyOptions = opts;
      },
      setSelectedCompanyId: id => {
        this.selectedCompanyId = id;
      },
      onFirstSelected: () => this.loadCampaigns(),
      onComplete: () => {
        if (this.companyOptions.length === 0) {
          this.loading = false;
        }
      },
    });
  }

  toggleCompanyDropdown(): void {
    toggleCompanyDropdown(this);
  }
  closeCompanyDropdown(): void {
    closeCompanyDropdown(this);
  }

  selectCompany(companyId: string): void {
    this.selectedCompanyId = companyId || '';
    this.isCompanyDropdownOpen = false;
    this.currentPage = 1;
    if (companyId) {
      this.loadCampaigns();
    } else {
      this.campaigns = [];
      this.totalItems = 0;
    }
  }

  /**
   * Load campaigns for the currently selected company, applying pagination, search, status and platform filters, and sorting; updates component state with results and handles errors.
   * @example
   * loadCampaigns()
   * void
   * @returns {void} Updates component state (campaigns, totalItems, loading, dragScrollConfig) and displays error messages on failure.
   **/
  private loadCampaigns(): void {
    if (!this.selectedCompanyId) {
      return;
    }

    this.loading = true;
    this.campaigns = [];
    this.totalItems = 0;

    const params: Record<string, any> = {
      page: this.currentPage.toString(),
      limit: this.itemsPerPage.toString(),
    };

    if (this.searchTerm) {
      params['search'] = this.searchTerm;
    }

    // Add status filter
    const statusFilters: string[] = [];
    if (this.appliedFilters.status.active) statusFilters.push('active');
    if (this.appliedFilters.status.scheduled) statusFilters.push('scheduled');
    if (this.appliedFilters.status.completed) statusFilters.push('completed');
    if (this.appliedFilters.status.draft) statusFilters.push('draft');

    if (statusFilters.length > 0) {
      params['status'] = statusFilters.join(',');
    }

    // Add platform filter
    const platformFilters: string[] = [];
    if (this.appliedFilters.platform?.email) platformFilters.push('email');
    if (this.appliedFilters.platform?.sms) platformFilters.push('sms');
    if (this.appliedFilters.platform?.facebook) platformFilters.push('facebook');
    if (this.appliedFilters.platform?.google) platformFilters.push('google');

    if (platformFilters.length > 0) {
      params['platform'] = platformFilters.join(',');
    }

    // Add sorting
    if (this.sortColumn && this.sortDirection !== 'none') {
      params['sort_by'] = this.sortColumn;
      params['sort_order'] = this.sortDirection;
    }

    this.apiService
      .getWithSuperAdminToken(
        `admin/super-admin/companies/${this.selectedCompanyId}/campaigns`,
        params
      )
      .subscribe({
        next: (response: any) => {
          this.loading = false;
          if (response.status && response.data) {
            if (response.data.campaigns && Array.isArray(response.data.campaigns)) {
              this.campaigns = this.mapApiCampaignsToTableRows(response.data.campaigns);
              this.totalItems = parseInt(response.data.pagination?.total || '0', 10);
            } else if (Array.isArray(response.data)) {
              this.campaigns = this.mapApiCampaignsToTableRows(response.data);
              this.totalItems = parseInt(response.pagination?.total_records || '0', 10);
            }
          } else {
            this.campaigns = [];
            this.totalItems = 0;
          }
          setTimeout(() => {
            this.dragScrollConfig =
              TableUtils.reinitializeDragScrolling(this.tableScrollWrapper) ??
              this.dragScrollConfig;
          }, 100);
        },
        error: error => {
          console.error('Error loading campaigns:', error);
          this.loading = false;
          this.campaigns = [];
          this.totalItems = 0;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load campaigns',
          });
        },
      });
  }

  /**
   * Map API campaign objects to table row objects, normalizing fields, formatting dates and offer types, and extracting performance and goals.
   * @example
   * mapApiCampaignsToTableRows([{ id: '1', name: 'Test Campaign', offer_type: 'discount', start_date: '2026-01-01', end_date: '2026-01-10' }])
   * [{ id: '1', name: 'Test Campaign', status: '', promoCode: '', offerType: 'discount', discount: '0', startDate: '2026-01-01', endDate: '2026-01-10', platforms: [], locations: [], segments: [], performance: undefined, goals: [] }]
   * @param {{any[]}} {{apiCampaigns}} - Array of raw campaign objects from the API to convert.
   * @returns {{CampaignTableRow[]}} Array of normalized CampaignTableRow objects ready for table consumption.
   **/
  private mapApiCampaignsToTableRows(apiCampaigns: any[]): CampaignTableRow[] {
    return apiCampaigns.map(campaign => ({
      id: campaign.id,
      name: campaign.name,
      status: campaign.status || campaign.campaign_status || '',
      promoCode: campaign.promo_code || '',
      offerType: this.formatOfferType(campaign.offer_type || ''),
      discount: campaign.offer_discount || campaign.max_discount || '0',
      startDate: this.formatDate(campaign.start_date || campaign.schedule_start_date),
      endDate: this.formatDate(campaign.end_date || campaign.schedule_end_date),
      platforms: campaign.platforms || [],
      locations:
        campaign.locations?.map((loc: any) => loc.branch || loc.name || loc.id || loc.city) || [],
      segments: campaign.segments?.map((seg: any) => seg.name || seg.id) || [],
      performance:
        campaign.performance || campaign.tracking
          ? {
              campaign_id: (campaign.performance || campaign.tracking)?.campaign_id || '',
              clicks: (campaign.performance || campaign.tracking)?.clicks || '0',
              conversions: (campaign.performance || campaign.tracking)?.conversions || '0',
              delivered: (campaign.performance || campaign.tracking)?.delivered || '0',
              failed: (campaign.performance || campaign.tracking)?.failed || '0',
              impressions: (campaign.performance || campaign.tracking)?.impressions || '0',
              opens: (campaign.performance || campaign.tracking)?.opens || '0',
              sent: (campaign.performance || campaign.tracking)?.sent || '0',
              total_revenue:
                (campaign.performance || campaign.tracking)?.total_revenue ||
                campaign.tracking?.total_revenue ||
                '0',
            }
          : undefined,
      goals:
        campaign.goals && Array.isArray(campaign.goals)
          ? campaign.goals.map((goal: any) => ({
              id: goal.id || '',
              campaign_id: goal.campaign_id || '',
              achieved: goal.achieved || 0,
              created_at: goal.created_at || '',
              end_date: goal.end_date || null,
              event: goal.event || '',
              goal_name: goal.goal_name || '',
              start_date: goal.start_date || null,
              status: goal.status !== undefined ? goal.status : true,
              target_value: goal.target_value || 0,
              timeframe: goal.timeframe || '',
              updated_at: goal.updated_at || '',
            }))
          : [],
    }));
  }

  private formatOfferType(offerType: string): string {
    switch (offerType) {
      case 'flat_discount':
        return 'Flat Discount';
      case 'percentage_discount':
        return 'Percentage Discount';
      default:
        return offerType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  }

  /**
   * Formats a schedule value as "Mon DD, YYYY", appending "hh:mm AM/PM" when it carries a
   * time-of-day.
   *
   * The time is read from the instant itself, never from the campaign's schedule_*_time fields:
   * schedule_start_time is a SERVER-LOCAL projection the API derives for the scheduler's
   * zone-less TIME column and schedule_end_time is not persisted at all, so appending them
   * showed an IST 5:00 PM pick as "11:30:00" against a UTC API.
   * @example
   * formatDate('2023-03-15T14:30:00.000Z')
   * Mar 15, 2023 08:00 PM
   * @param {{string}} {{date}} - Date or instant string to format; returns '-' if falsy.
   * @returns {{string}} Formatted value (e.g., "Mar 15, 2023" or "Mar 15, 2023 08:00 PM"), or the original input if parsing fails.
   **/
  private formatDate(date: string): string {
    if (!date) return '-';
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return date;

    const formattedDate = dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

    // Date-only values (YYYY-MM-DD) carry no time to show.
    if (!/\d{2}:\d{2}/.test(date)) {
      return formattedDate;
    }

    const formattedTime = dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return `${formattedDate} ${formattedTime}`;
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    this.loadCampaigns();
  }

  onFilter(): void {
    // Sync filterCategories with appliedFilters before opening modal
    this.syncFilterCategoriesWithAppliedFilters();
    this.showFilterModal = true;
  }

  /**
   * Syncs filter category option selections with the component's appliedFilters state.
   * @example
   * syncFilterCategoriesWithAppliedFilters()
   * undefined
   * @param {{void}} none - No parameters.
   * @returns {{void}} Void.
   **/
  private syncFilterCategoriesWithAppliedFilters(): void {
    // Sync status
    const statusCategory = this.filterCategories.find(cat => cat.id === 'status');
    if (statusCategory) {
      statusCategory.options.forEach(option => {
        if (option.value === 'active') {
          option.selected = this.appliedFilters.status.active;
        } else if (option.value === 'scheduled') {
          option.selected = this.appliedFilters.status.scheduled;
        } else if (option.value === 'completed') {
          option.selected = this.appliedFilters.status.completed;
        } else if (option.value === 'draft') {
          option.selected = this.appliedFilters.status.draft;
        }
      });
    }

    // Sync platform
    const platformCategory = this.filterCategories.find(cat => cat.id === 'platform');
    if (platformCategory) {
      platformCategory.options.forEach(option => {
        if (option.value === 'email') {
          option.selected = this.appliedFilters.platform.email;
        } else if (option.value === 'sms') {
          option.selected = this.appliedFilters.platform.sms;
        } else if (option.value === 'facebook') {
          option.selected = this.appliedFilters.platform.facebook;
        } else if (option.value === 'google') {
          option.selected = this.appliedFilters.platform.google;
        }
      });
    }
  }

  onCloseFilterModal(): void {
    this.showFilterModal = false;
  }

  /**
   * Apply selected filter options from filter modal to the component's campaign filters, update pagination and UI state, and reload campaigns.
   * @example
   * nFilterModalApply([{ id: 'status', options: [{ value: 'active', selected: true }] }])
   * undefined
   * @param {{FilterCategory[]}} {{categories}} - Array of filter category objects. Each category should contain an 'id' (e.g. 'status'|'platform') and an 'options' array where each option has 'value' and 'selected' boolean.
   * @returns {{void}} No return value. Updates this.appliedFilters, sets this.currentPage to 1, hides the filter modal, and calls this.loadCampaigns() as side effects.
   **/
  onFilterModalApply(categories: FilterCategory[]): void {
    const filters: any = getDefaultCampaignFilters();

    categories.forEach(category => {
      const selectedOptions = category.options.filter(option => option.selected);

      switch (category.id) {
        case 'status':
          selectedOptions.forEach(option => {
            if (option.value === 'active') filters.status.active = true;
            if (option.value === 'scheduled') filters.status.scheduled = true;
            if (option.value === 'completed') filters.status.completed = true;
            if (option.value === 'draft') filters.status.draft = true;
          });
          break;
        case 'platform':
          selectedOptions.forEach(option => {
            if (option.value === 'email') filters.platform.email = true;
            if (option.value === 'sms') filters.platform.sms = true;
            if (option.value === 'facebook') filters.platform.facebook = true;
            if (option.value === 'google') filters.platform.google = true;
          });
          break;
      }
    });

    this.appliedFilters = filters;
    this.currentPage = 1;
    this.showFilterModal = false;
    this.loadCampaigns();
  }

  get hasActiveFilters(): boolean {
    return this.getActiveFilterCount() > 0;
  }

  getActiveFilterCount(): number {
    return countActiveFilterValues(this.appliedFilters.status, this.appliedFilters.platform);
  }

  get hasSearchOrFilters(): boolean {
    return (Boolean(this.searchTerm) && this.searchTerm.trim().length > 0) || this.hasActiveFilters;
  }

  get paginationData(): PaginationData {
    return buildPaginationData(
      this.currentPage,
      this.totalPages,
      this.totalItems,
      this.itemsPerPage
    );
  }

  get totalPages(): number {
    return calculateTotalPages(this.totalItems, this.itemsPerPage);
  }

  onPageChange(page: number): void {
    applyPageChange(
      {
        totalPages: this.totalPages,
        setCurrentPage: p => {
          this.currentPage = p;
        },
        loadOrders: () => this.loadCampaigns(),
      },
      page
    );
  }

  onItemsPerPageChange(newItemsPerPage: number): void {
    applyItemsPerPageChange(
      {
        setItemsPerPage: v => {
          this.itemsPerPage = v;
        },
        setCurrentPage: p => {
          this.currentPage = p;
        },
        loadOrders: () => this.loadCampaigns(),
      },
      newItemsPerPage
    );
  }

  sortByName(): void {
    if (this.sortColumn === 'name') {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = 'name';
      this.sortDirection = 'asc';
    }
    this.loadCampaigns();
  }

  getSortIcon(column: string): string {
    if (this.sortColumn === column) {
      const iconMap: Record<string, string> = {
        name:
          this.sortDirection === 'asc'
            ? '/assets/images/sort-ascending-numbers.svg'
            : '/assets/images/sort-descending-numbers.svg',
      };
      return iconMap[column] || '/assets/images/tabler_arrows_sort.png';
    }
    return '/assets/images/tabler_arrows_sort.png';
  }

  getStatusClass(status: string): string {
    const statusMap: Record<string, string> = {
      active: 'status-green',
      scheduled: 'status-blue',
      completed: 'status-purple',
      draft: 'status-gray',
    };
    return statusMap[status.toLowerCase()] || 'status-gray';
  }

  getStatusLabel(status: string): string {
    const statusMap: Record<string, string> = {
      active: 'Active',
      scheduled: 'Scheduled',
      completed: 'Completed',
      draft: 'Draft',
    };
    return statusMap[status.toLowerCase()] || status;
  }

  formatPlatforms(platforms: string[]): string {
    if (!platforms || platforms.length === 0) return '-';
    return platforms.map(p => p.toUpperCase()).join(', ');
  }

  formatLocations(locations: string[]): string {
    if (!locations || locations.length === 0) return '-';
    if (locations.length > 2) {
      return `${locations.slice(0, 2).join(', ')} +${locations.length - 2}`;
    }
    return locations.join(', ');
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
